import { useState } from "react"

import { useTranslation } from "react-i18next"

import { ChevronDown, ChevronUp, Globe, MousePointerClick } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { logger } from "@/lib/logger"

type WebScrapeResultsProps = {
  urls: string[]
  demoMode?: boolean
}

const MAX_INITIAL_RESULTS = 4

export function WebScrapeResults({ urls, demoMode = false }: WebScrapeResultsProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  if (urls.length === 0) {
    return null
  }

  const visibleResults = isExpanded ? urls : urls.slice(0, MAX_INITIAL_RESULTS)

  const hasMoreResults = urls.length > MAX_INITIAL_RESULTS

  return (
    <section aria-label="Scraped web pages" className="flex min-w-0 flex-col gap-3">
      <div className="grid min-w-0 gap-2" role="list">
        {visibleResults.map((url, index) => (
          <WebScrapeResultItem key={url} demoMode={demoMode} index={index} url={url} />
        ))}
      </div>

      {hasMoreResults && (
        <div className="flex justify-center pt-1">
          <Button
            aria-controls="web-scrape-results-list"
            aria-expanded={isExpanded}
            className="text-muted-foreground focus:ring-ring h-8 px-4 text-xs font-medium focus:ring-offset-1"
            size="sm"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="mr-1.5 size-3.5" strokeWidth={2} />
                {t("arrowbutton.show_less")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-1.5 size-3.5" strokeWidth={2} />
                {t("arrowbutton.show_more")} ({urls.length - MAX_INITIAL_RESULTS})
              </>
            )}
          </Button>
        </div>
      )}
    </section>
  )
}

type WebScrapeResultItemProps = {
  url: string
  index?: number
  demoMode?: boolean
}

function WebScrapeResultItem({ url, index = 0, demoMode = false }: WebScrapeResultItemProps) {
  let baseUrl = ""
  let title = ""
  try {
    const urlObj = new URL(url)
    baseUrl = urlObj.hostname
    title = urlObj.pathname === "/" ? baseUrl : `${baseUrl}${urlObj.pathname}`
  } catch (error) {
    logger.error(`Invalid URL: ${url}`, { error })
    baseUrl = url
    title = url
  }
  const iconSrc = `https://icons.duckduckgo.com/ip3/${baseUrl}.ico`

  const content = (
    <article
      className="border-border/50 animate-in fade-in-0 slide-in-from-bottom-1 hover:bg-muted/50 relative flex items-center gap-3 rounded-lg border p-3 transition-[background-color,box-shadow,scale] duration-200 group-hover/result:scale-[1.01] hover:shadow-sm"
      role="listitem"
    >
      <div className="ring-border/20 group-hover/result:ring-border/40 flex size-7 shrink-0 items-center justify-center rounded-md ring-1 transition-shadow">
        <img
          alt={`${baseUrl} favicon`}
          className="size-4 rounded-sm"
          src={iconSrc}
          onError={(e) => {
            e.currentTarget.style.display = "none"
            e.currentTarget.nextElementSibling?.classList.remove("hidden")
          }}
        />
        <Globe className="text-muted-foreground hidden size-4" strokeWidth={1.5} />
      </div>

      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-foreground/90 group-hover/result:text-foreground max-w-[100px] shrink-0 truncate text-sm font-medium">
            {baseUrl}
          </span>
          <div className="bg-muted-foreground/40 h-1 w-1 shrink-0 rounded-full" />
          <span className="text-muted-foreground/80 min-w-0 flex-1 truncate text-xs">{title}</span>
        </div>
        <div className="text-muted-foreground/60 w-full truncate text-xs">{url}</div>
      </div>

      {!demoMode && (
        <div className="flex shrink-0 opacity-0 transition-opacity group-hover/result:opacity-100">
          <MousePointerClick className="text-muted-foreground size-4" strokeWidth={1.5} />
        </div>
      )}
    </article>
  )

  if (demoMode) {
    return (
      <div
        className="group/result block min-w-0 rounded-lg"
        style={{ animationDelay: `${index * 50}ms` }}
      >
        {content}
      </div>
    )
  }

  return (
    <a
      aria-label={`Visit ${title}`}
      className="group/result block min-w-0 rounded-lg"
      href={url}
      referrerPolicy="no-referrer"
      rel="noopener noreferrer"
      style={{ animationDelay: `${index * 50}ms` }}
      target="_blank"
    >
      {content}
    </a>
  )
}
